import React from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { TrackerBranding, TrackerItemView, TrackerSectionView } from '../../types/gotypes'
import { StepRail, StatusBadge, NoteLine, Reveal, StepKey } from './atoms'

const Lead: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => {
  const theme = useTheme()
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: theme.palette.text.secondary }}>
      {icon}
      <Typography sx={{ fontSize: 12.5 }}>{label || 'TBC'}</Typography>
    </Box>
  )
}

const DetailLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme()
  return (
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: theme.palette.text.disabled, mb: 0.75 }}>
      {children}
    </Typography>
  )
}

const DetailBlock: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const theme = useTheme()
  if (!value) return null
  return (
    <Box>
      <DetailLabel>{label}</DetailLabel>
      <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: theme.palette.text.secondary, whiteSpace: 'pre-wrap' }}>
        {value}
      </Typography>
    </Box>
  )
}

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
    <Box component="span" sx={{ fontWeight: 600 }}>{label}:</Box> {value}
  </Typography>
)

const ItemCard: React.FC<{ item: TrackerItemView; accent: string; index: number }> = ({ item, accent, index }) => {
  const theme = useTheme()

  // Details mirror the old stage card: description (scope), acceptance
  // criteria, and a details column. The objective sits on the card itself.
  const hasDetails = Boolean(item.scope || item.acceptanceCriteria || item.body || item.uatDate || item.acceptedDate)

  return (
    <Reveal index={index}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${item.done ? alpha(theme.palette.success.main, 0.4) : theme.palette.divider}`,
          transition: 'box-shadow 0.25s ease, transform 0.25s cubic-bezier(0.22,1,0.36,1), border-color 0.25s ease',
          '&:hover': { boxShadow: '0 4px 16px rgba(15,23,42,0.06)', borderColor: alpha(accent, 0.3), transform: 'translateY(-2px)' },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {item.number != null && (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 15,
                    flexShrink: 0,
                    color: item.done ? theme.palette.success.main : accent,
                    bgcolor: alpha(item.done ? theme.palette.success.main : accent, 0.12),
                    boxShadow: item.done ? `0 0 0 3px ${alpha(theme.palette.success.main, 0.18)}` : 'none',
                  }}
                >
                  {item.number}
                </Box>
              )}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>{item.title}</Typography>
                  {item.jobId && <Chip size="small" label={`Job ${item.jobId}`} sx={{ fontWeight: 600 }} />}
                </Box>
                {item.objective && (
                  <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                    “{item.objective}”
                  </Typography>
                )}
              </Box>
            </Box>
            <StatusBadge status={item.status} />
          </Box>

          <Box sx={{ px: { xs: 0, sm: 2 } }}>
            <StepRail steps={item.steps} accentColor={accent} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Lead icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />} label={item.poc} />
          </Box>

          <NoteLine note={item.latestNote} />
        </Box>

        {hasDetails && (
          <Accordion disableGutters elevation={0} square sx={{ '&:before': { display: 'none' }, bgcolor: alpha(theme.palette.text.primary, 0.015) }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.secondary }}>Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
                <DetailBlock label="DESCRIPTION" value={item.scope} />
                <DetailBlock label="ACCEPTANCE CRITERIA" value={item.acceptanceCriteria} />
                <Box>
                  <DetailLabel>DETAILS</DetailLabel>
                  <DetailRow label="Module lead" value={item.poc || 'TBC'} />
                  {item.uatDate && <DetailRow label="UAT date" value={item.uatDate} />}
                  {item.acceptedDate && <DetailRow label="Accepted" value={item.acceptedDate} />}
                </Box>
              </Box>
              <Box sx={{ mt: item.body ? 2 : 0 }}>
                <DetailBlock label="NOTES" value={item.body} />
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>
    </Reveal>
  )
}

// SectionView renders one tracker section: its header (name, description),
// the workflow step key, and its items in the order the server gives
// them. One component serves every section — sections are generic, not
// specific "streams" (Adam, 23 Sep).
export const SectionView: React.FC<{ section: TrackerSectionView; branding: TrackerBranding }> = ({ section, branding }) => {
  const theme = useTheme()
  const accent = branding.primaryColor || theme.palette.primary.main

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        {section.name}
      </Typography>
      {section.description && (
        <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary, mb: 2 }}>
          {section.description}
        </Typography>
      )}

      <StepKey legend={section.stepLegend} accent={accent} />

      <Stack spacing={2}>
        {section.items.map((item, i) => (
          <ItemCard key={item.id} item={item} accent={accent} index={i} />
        ))}
        {section.items.length === 0 && (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px dashed ${theme.palette.divider}`, textAlign: 'center', color: theme.palette.text.secondary }}>
            No items yet.
          </Paper>
        )}
      </Stack>
    </Box>
  )
}

export default SectionView
